import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LoadingPage } from './components/ui';
import { useAuth } from './lib/auth';
import { AuthPage } from './pages/AuthPage';
import { NeighborhoodPage } from './pages/NeighborhoodPage';
import { ProfilePage } from './pages/ProfilePage';
import { ArchitecturePage } from './pages/ArchitecturePage';
import { ArchitectureGuidePage } from './pages/ArchitectureGuidePage';
import { GuidelinesPage } from './pages/GuidelinesPage';
import { RequestsPage } from './pages/RequestsPage';
import { EventsPage } from './pages/EventsPage';
import { BoardHubPage } from './pages/BoardHubPage';
import { ResidentsPage } from './pages/ResidentsPage';
import { FinancialsPage } from './pages/FinancialsPage';
import { NewsletterPage } from './pages/NewsletterPage';
import { CommunityPage } from './pages/CommunityPage';
import { CommunityInfoPage } from './pages/CommunityInfoPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { VisitorAnalyticsPage } from './pages/admin/VisitorAnalyticsPage';
import { AppAnalyticsPage } from './pages/admin/AppAnalyticsPage';
import { PlatformSettingsPage } from './pages/admin/PlatformSettingsPage';
import { ViolationsPage } from './pages/ViolationsPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { BylawsPage } from './pages/BylawsPage';
import { ArchResourcesPage } from './pages/ArchResourcesPage';
import { KnowledgeSourcesPage } from './pages/KnowledgeSourcesPage';

function BoardOnly({ children }: { children: React.ReactNode }) {
  const { isBoard } = useAuth();
  return isBoard ? <>{children}</> : <Navigate to="/" replace />;
}

function FinanceOnly({ children }: { children: React.ReactNode }) {
  const { canUseFinance } = useAuth();
  return canUseFinance ? <>{children}</> : <Navigate to="/" replace />;
}

/** Board, Architecture Review Board, or a Super Admin managing the community. */
function ReviewerOnly({ children }: { children: React.ReactNode }) {
  const { isReviewer, isSuperAdmin } = useAuth();
  return isReviewer || isSuperAdmin ? <>{children}</> : <Navigate to="/architecture" replace />;
}

function SuperOnly({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin ? <>{children}</> : <Navigate to="/" replace />;
}

function HomeRedirect() {
  const { user, isSuperAdmin } = useAuth();
  if (!user?.communityId && isSuperAdmin) return <Navigate to="/admin" replace />;
  return <NeighborhoodPage />;
}

export function App() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingPage label="Checking your session…" />;
  if (!user) {
    return (
      <Routes>
        <Route path="/reset" element={<ResetPasswordPage />} />
        <Route path="*" element={<AuthPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/reset" element={<ResetPasswordPage />} />
      <Route element={<Layout />}>
        <Route index element={<HomeRedirect />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="bylaws" element={<BylawsPage />} />
        <Route path="architecture" element={<ArchitecturePage />} />
        <Route path="architecture/guide" element={<ArchitectureGuidePage />} />
        <Route path="architecture/guidelines" element={<GuidelinesPage />} />
        <Route path="architecture/resources" element={<ArchResourcesPage />} />
        <Route
          path="architecture/sources"
          element={
            <ReviewerOnly>
              <KnowledgeSourcesPage />
            </ReviewerOnly>
          }
        />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="violations" element={<ViolationsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route
          path="board"
          element={
            <BoardOnly>
              <BoardHubPage />
            </BoardOnly>
          }
        />
        <Route
          path="board/residents"
          element={
            <BoardOnly>
              <ResidentsPage />
            </BoardOnly>
          }
        />
        <Route
          path="financials"
          element={
            <FinanceOnly>
              <FinancialsPage />
            </FinanceOnly>
          }
        />
        <Route
          path="newsletter"
          element={
            <BoardOnly>
              <NewsletterPage />
            </BoardOnly>
          }
        />
        <Route
          path="community"
          element={
            <BoardOnly>
              <CommunityPage />
            </BoardOnly>
          }
        />
        <Route path="community-info" element={<CommunityInfoPage />} />
        <Route
          path="admin"
          element={
            <SuperOnly>
              <SuperAdminPage />
            </SuperOnly>
          }
        />
        <Route
          path="admin/visitors"
          element={
            <SuperOnly>
              <VisitorAnalyticsPage />
            </SuperOnly>
          }
        />
        <Route
          path="admin/usage"
          element={
            <SuperOnly>
              <AppAnalyticsPage />
            </SuperOnly>
          }
        />
        <Route
          path="admin/settings"
          element={
            <SuperOnly>
              <PlatformSettingsPage />
            </SuperOnly>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
